import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { DemoBadge, Empty, ErrorMsg, KV, Loading, PageHead, Section, StatusMark, Time, TzNote, Unknown } from '../components/ui';
import { api, errorText, newIdempotencyKey } from '../lib/api';
import { explainJobError, fieldValue, JOB_TYPE_LABEL, jobType, reportingTz, STATUS_LABEL, TERMINAL } from '../lib/format';
import { useApi, useSession } from '../lib/session';
import type { ImportBatch, Job, ScheduleRow, WorkerStatus } from '../lib/types';
import { CollectOutputs } from './studio/OtherTabs';

const JOB_STATUSES = ['queued', 'running', 'waiting_review', 'succeeded', 'partial', 'failed', 'cancelled'];

function WorkerRows({ workers }: { workers: WorkerStatus['workers'] }) {
  return (
    <ul className="rows small">
      {workers.map((x) => (
        <li key={x.id}>
          <span className="mono">{x.host}</span> · {x.state} · {x.running_jobs} running · last seen <Time iso={x.seen_at} relative />
        </li>
      ))}
    </ul>
  );
}

function WorkerPanel() {
  const { data, error, reload } = useApi<WorkerStatus>('/api/worker');
  const { user } = useSession();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const t = window.setInterval(reload, 15000);
    return () => window.clearInterval(t);
  }, [reload]);

  if (!data) return error ? <ErrorMsg>{error}</ErrorMsg> : <Loading what="Checking the worker" />;
  const w = data.settings;
  const state = data.workers[0]?.state;
  const liveWorkers = data.workers.filter((x) => x.state !== 'stopped');
  const stoppedWorkers = data.workers.filter((x) => x.state === 'stopped');

  async function setPaused(paused: boolean) {
    setBusy(true);
    setErr('');
    try {
      await api('/api/settings/worker', {
        method: 'PUT',
        body: { value: { ...w, paused, pause_reason: paused ? reason.trim() || 'rendering priority' : null } },
      });
      setReason('');
      reload();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Worker" aside={<span className="muted small">refreshes every 15 s</span>}>
      <div className="worker-grid">
        <div>
          <p className="worker-line">
            <StatusMark status={data.alive ? (w.paused ? 'paused' : state === 'quiet_hours' ? 'paused' : 'running') : 'failed'} />{' '}
            {data.alive ? (
              <>
                Alive · {state === 'quiet_hours' ? 'quiet hours (not starting jobs)' : w.paused ? 'paused (not starting jobs)' : state ?? 'running'}
              </>
            ) : data.workers.length ? (
              'Not responding: no heartbeat in the last 30 seconds. Queued jobs wait.'
            ) : (
              'No worker has reported in the last 24 hours. Queued jobs wait until one runs.'
            )}
          </p>
          {liveWorkers.length > 0 && <WorkerRows workers={liveWorkers} />}
          {stoppedWorkers.length > 0 && (
            <details className="small fold">
              <summary className="muted">
                {stoppedWorkers.length} stopped worker {stoppedWorkers.length === 1 ? 'process' : 'processes'} (earlier runs)
              </summary>
              <WorkerRows workers={stoppedWorkers} />
            </details>
          )}
          <KV
            rows={[
              ['Concurrency', `${w.concurrency} job${w.concurrency === 1 ? '' : 's'} at a time`],
              ['Quiet hours', w.quiet_hours.enabled ? `${w.quiet_hours.start}–${w.quiet_hours.end} (${reportingTz()})` : 'off'],
            ]}
          />
          <p className="small">
            <Link to="/settings#worker">Change in Settings</Link>
          </p>
        </div>
        <div className="pause-box">
          <h3 className="sub">Rendering priority</h3>
          <p className="small muted">Pause local jobs while the machine renders. Running jobs finish; no new job starts until you resume.</p>
          {w.paused ? (
            <>
              <p className="small">
                Paused{w.pause_reason ? `: ${w.pause_reason}` : ''}.
              </p>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void setPaused(false)}>
                Resume local jobs
              </button>
            </>
          ) : (
            <>
              <label className="field">
                <span>Reason (optional)</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="rendering priority" />
              </label>
              <button type="button" className="btn" disabled={busy} onClick={() => void setPaused(true)}>
                Pause local jobs
              </button>
            </>
          )}
          {user?.role !== 'owner' && <p className="muted small">Editors may pause and resume; other worker settings are the owner’s.</p>}
          <ErrorMsg>{err}</ErrorMsg>
        </div>
      </div>
    </Section>
  );
}

function Outputs({ job }: { job: Job }) {
  const out = job.outputs ?? {};
  if (!Object.keys(out).length) return <Empty>No outputs recorded{TERMINAL.includes(job.status) ? '' : ' yet'}.</Empty>;
  if (job.type === 'discover_country') {
    const n = (k: string) => (typeof out[k] === 'number' ? (out[k] as number) : 0);
    return (
      <div className="small">
        <p>
          <span className="num">{n('created')}</span> created · <span className="num">{n('matched')}</span> matched ·{' '}
          <span className="num">{n('suppressed')}</span> suppressed · <span className="num">{n('invalid')}</span> invalid ·{' '}
          <span className="num">{n('collection_failed')}</span> sites unreadable
        </p>
        {typeof out.batch_id === 'string' && (
          <p>
            <Link to={`/competitors?batch_id=${out.batch_id}&include_dismissed=1`}>Review this batch in Competitors</Link>
          </p>
        )}
      </div>
    );
  }
  if ('pages_attempted' in out) return <CollectOutputs out={out} />;
  return (
    <KV rows={Object.entries(out).map(([k, v]) => [k.replace(/_/g, ' '), fieldValue(v)])} />
  );
}

function JobDrawer({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { data: job, error, reload } = useApi<Job>(`/api/jobs/${id}`);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const running = job ? !TERMINAL.includes(job.status) : false;

  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(reload, 3000);
    return () => window.clearInterval(t);
  }, [running, reload]);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Esc closes; registered once (M-090), reads the latest onClose through a ref.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.fb-dialog')) onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function cancel() {
    setBusy(true);
    setErr('');
    try {
      await api(`/api/jobs/${id}/cancel`, { method: 'POST' });
      reload();
      onChanged();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  async function rerun() {
    setBusy(true);
    setErr('');
    try {
      const r = await api<{ job_id: string }>(`/api/jobs/${id}/rerun`, { method: 'POST', idempotencyKey: newIdempotencyKey() });
      onChanged();
      navigate(`/jobs/${r.job_id}`);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const r = job?.resources;
  const notMeasured = <Unknown>not measured</Unknown>;
  return (
    <div className="drawer-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label="Job details">
        <header className="modal-head">
          <h2>{job ? jobType(job.type) : 'Job'}</h2>
          <button type="button" className="btn ghost small" onClick={onClose} aria-label="Close job details">
            Close
          </button>
        </header>
        {!job ? (
          error ? <ErrorMsg>{error}</ErrorMsg> : <Loading what="Loading the job" />
        ) : (
          <div className="drawer-body">
            <p>
              <StatusMark status={job.status} />
              {job.cancel_requested && !TERMINAL.includes(job.status) && <span className="muted"> · cancel requested</span>}
              {job.needs_attention && <span className="err-text"> · needs attention</span>}
            </p>
            {job.error && (
              <p className="msg err small">
                {explainJobError(job.error) && (
                  <>
                    {explainJobError(job.error)}
                    <br />
                  </>
                )}
                <span className="mono">{job.error}</span>
              </p>
            )}
            <div className="row gap">
              <button type="button" className="btn small" disabled={busy || TERMINAL.includes(job.status)} onClick={() => void cancel()}>
                Cancel job
              </button>
              <button type="button" className="btn small" disabled={busy || !TERMINAL.includes(job.status)} onClick={() => void rerun()}>
                Rerun
              </button>
            </div>
            <ErrorMsg>{err}</ErrorMsg>
            <h3 className="sub">Summary</h3>
            <KV
              rows={[
                ['Studio', job.studio_id ? <Link to={`/competitors/${job.studio_id}`}>{job.studio_name ?? 'studio'}</Link> : <span className="muted">none</span>],
                ['Created', <Time iso={job.created_at} />],
                ['Started', <Time iso={job.started_at} />],
                ['Finished', <Time iso={job.finished_at} />],
                ['Attempts', `${job.attempts} of ${job.max_attempts}`],
                ...(job.next_attempt_at && !TERMINAL.includes(job.status) ? ([['Next attempt', <Time iso={job.next_attempt_at} />]] as [string, React.ReactNode][]) : []),
                ['Schedule', job.schedule_id ? 'scheduled run' : 'started by hand'],
              ]}
            />
            {running && job.progress && Object.keys(job.progress).length > 0 && (
              <p className="small">
                Progress: {Object.entries(job.progress).map(([k, v]) => `${k} ${fieldValue(v)}`).join(' · ')}
              </p>
            )}
            <h3 className="sub">Input, scope and limits</h3>
            <KV
              rows={[
                ...Object.entries(job.input ?? {}).map(([k, v]) => [`input · ${k.replace(/_/g, ' ')}`, fieldValue(v)] as [string, string]),
                ...Object.entries(job.source_scope ?? {}).map(([k, v]) => [`scope · ${k.replace(/_/g, ' ')}`, fieldValue(v)] as [string, string]),
                ['Page limit', job.page_limit ?? '—'],
                ['Candidate limit', job.candidate_limit ?? '—'],
                ['Timeout', job.timeout_s ? `${job.timeout_s} s` : '—'],
              ]}
            />
            <h3 className="sub">Outputs</h3>
            <Outputs job={job} />
            <h3 className="sub">Resources</h3>
            <KV
              rows={[
                ['Wall time', r?.wall_seconds != null ? `${r.wall_seconds.toFixed(1)} s` : notMeasured],
                ['HTTP requests', r?.http_requests != null ? String(r.http_requests) : notMeasured],
                ['Model tokens in / out', r?.tokens_in != null || r?.tokens_out != null ? `${r?.tokens_in ?? '?'} / ${r?.tokens_out ?? '?'}` : notMeasured],
                ['Cost (actual)', r?.cost_actual_eur != null ? `€${r.cost_actual_eur}` : notMeasured],
                ['Cost (estimate)', r?.cost_estimate_eur != null ? `€${r.cost_estimate_eur}` : notMeasured],
              ]}
            />
            <p className="muted small">No model is called in this release, so tokens and cost are not measured (which is not the same as zero).</p>
            <h3 className="sub">Events</h3>
            {!job.events?.length ? (
              <Empty>No events logged.</Empty>
            ) : (
              <ol className="events">
                {job.events.map((e, i) => (
                  <li key={i} className={`ev ev-${e.level}`}>
                    <span className="muted small num">
                      <Time iso={e.at} />
                    </span>{' '}
                    <span className="ev-level">{e.level}</span> {e.message}
                  </li>
                ))}
              </ol>
            )}
            <details className="raw">
              <summary className="small">Raw job record (JSON)</summary>
              <pre>{JSON.stringify(job, null, 2)}</pre>
            </details>
          </div>
        )}
      </aside>
    </div>
  );
}

function Schedules() {
  const { data, error, reload } = useApi<{ items: ScheduleRow[] }>('/api/schedules?include_ended=true');
  const [err, setErr] = useState('');
  async function setState(id: string, status: 'active' | 'paused') {
    setErr('');
    try {
      await api(`/api/schedules/${id}/state`, { method: 'POST', body: { status } });
      reload();
    } catch (e) {
      setErr(errorText(e));
    }
  }
  return (
    <Section title="Schedules" aside={<TzNote />}>
      <ErrorMsg>{error || err}</ErrorMsg>
      {!data ? (
        <Loading />
      ) : data.items.length === 0 ? (
        <Empty>No schedules. Promoting a studio to Deep creates one per collected channel.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Studio</th>
                <th>Task</th>
                <th className="num">Every</th>
                <th>Next run</th>
                <th>Last success</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.id} className={s.status === 'ended' ? 'dim' : ''}>
                  <td>
                    <StatusMark status={s.status} />
                    {s.ended_reason && <div className="small muted">{s.ended_reason}</div>}
                  </td>
                  <td>{s.studio_id ? <Link to={`/competitors/${s.studio_id}`}>{s.studio_name ?? 'studio'}</Link> : '—'}</td>
                  <td>
                    {jobType(s.task_type)} <span className="muted">({s.channel || 'all'})</span>
                  </td>
                  <td className="num">{s.interval_days} d</td>
                  <td>{s.status === 'ended' ? '—' : <Time iso={s.next_run_at} />}</td>
                  <td>{s.last_success_at ? <Time iso={s.last_success_at} /> : <span className="muted">never</span>}</td>
                  <td>
                    {s.status === 'active' && (
                      <button type="button" className="btn small" onClick={() => void setState(s.id, 'paused')}>
                        Pause
                      </button>
                    )}
                    {s.status === 'paused' && (
                      <button type="button" className="btn small" onClick={() => void setState(s.id, 'active')}>
                        Resume
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function Batches({ onChanged }: { onChanged: () => void }) {
  const { data, error, reload } = useApi<{ items: ImportBatch[] }>('/api/import-batches');
  const [err, setErr] = useState('');
  const pending = (data?.items ?? []).filter((b) => !b.reviewed_at);
  async function reviewed(id: string) {
    setErr('');
    try {
      await api(`/api/import-batches/${id}/reviewed`, { method: 'POST' });
      reload();
      onChanged();
    } catch (e) {
      setErr(errorText(e));
    }
  }
  return (
    <Section title="Batches awaiting review" id="batches">
      <ErrorMsg>{error || err}</ErrorMsg>
      {!data ? (
        <Loading />
      ) : pending.length === 0 ? (
        <Empty>Every import and discovery batch has been reviewed.</Empty>
      ) : (
        <ul className="rows">
          {pending.map((b) => (
            <li key={b.id} className="batch-row">
              <div>
                <Link to={`/competitors?batch_id=${b.id}&include_dismissed=1`}>{b.label}</Link> {b.is_demo && <DemoBadge />}
                <div className="muted small">
                  {b.kind} · <span className="num">{String(b.counts.created ?? 0)}</span> created, <span className="num">{String(b.counts.matched ?? 0)}</span>{' '}
                  matched, <span className="num">{String(b.counts.invalid ?? 0)}</span> invalid · <Time iso={b.created_at} />
                  {b.job_id && (
                    <>
                      {' '}
                      · <Link to={`/jobs/${b.job_id}`}>job</Link>
                    </>
                  )}
                </div>
              </div>
              <button type="button" className="btn small" onClick={() => void reviewed(b.id)}>
                Mark reviewed
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

export function Jobs() {
  const { jobId } = useParams();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const status = sp.get('status') ?? '';
  const type = sp.get('type') ?? '';
  const q = new URLSearchParams();
  if (status) q.set('status', status);
  if (type) q.set('type', type);
  q.set('limit', '200');
  const jobs = useApi<{ items: Job[]; counts: Record<string, number> }>(`/api/jobs?${q}`);
  const counts = jobs.data?.counts ?? {};
  const setFilter = (k: string, v: string) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n, { replace: true });
  };

  return (
    <div className="page wide">
      <PageHead title="Jobs" meta={<TzNote />} />
      <WorkerPanel />

      <Section title="Jobs">
        <ul className="counts">
          {JOB_STATUSES.map((s) => (
            <li key={s}>
              <button type="button" className={`count-btn${status === s ? ' on' : ''}`} onClick={() => setFilter('status', status === s ? '' : s)}>
                <span className="num">{counts[s] ?? 0}</span> {STATUS_LABEL[s].toLowerCase()}
              </button>
            </li>
          ))}
        </ul>
        <div className="filters">
          <label className="select-filter">
            <span>Status</span>
            <select value={status} onChange={(e) => setFilter('status', e.target.value)}>
              <option value="">all</option>
              {JOB_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="select-filter">
            <span>Type</span>
            <select value={type} onChange={(e) => setFilter('type', e.target.value)}>
              <option value="">all</option>
              {Object.entries(JOB_TYPE_LABEL).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
        </div>
        <ErrorMsg>{jobs.error}</ErrorMsg>
        {!jobs.data ? (
          <Loading what="Loading jobs" />
        ) : jobs.data.items.length === 0 ? (
          <Empty>{status || type ? 'No job matches these filters.' : 'No job has run yet.'}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table jobs">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Studio</th>
                  <th>Created</th>
                  <th>Finished</th>
                  <th className="num">Attempts</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {jobs.data.items.map((j) => (
                  <tr key={j.id} className={j.id === jobId ? 'selected' : ''}>
                    <td>
                      <StatusMark status={j.status} />
                    </td>
                    <td>
                      <Link to={`/jobs/${j.id}${sp.toString() ? `?${sp}` : ''}`}>{jobType(j.type)}</Link>
                    </td>
                    <td>{j.studio_id ? <Link to={`/competitors/${j.studio_id}`}>{j.studio_name ?? 'studio'}</Link> : <span className="muted">—</span>}</td>
                    <td>
                      <Time iso={j.created_at} />
                    </td>
                    <td>
                      <Time iso={j.finished_at} />
                    </td>
                    <td className="num">
                      {j.attempts}/{j.max_attempts}
                    </td>
                    <td className="small err-text clip" title={j.error ?? undefined}>
                      {explainJobError(j.error) ?? j.error ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Schedules />
      <Batches onChanged={jobs.reload} />

      {jobId && <JobDrawer key={jobId} id={jobId} onClose={() => navigate(`/jobs${sp.toString() ? `?${sp}` : ''}`)} onChanged={jobs.reload} />}
    </div>
  );
}
